import { FC, useEffect } from 'react'
import { TextField, Button, Box, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import { LoadingButton } from '@mui/lab'
import * as Yup from 'yup'
import { useQueryClient } from '@tanstack/react-query'
import { useIntl } from 'react-intl'
import { MarkEmailReadOutlined } from '@mui/icons-material'

import { useUserPasswordRequestReset } from 'services/register/register.hooks'
import { FieldLabel, submitButtonSx } from 'components/Helpdesk/FormPrimitives'
import theme from 'styles/theme'

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    backgroundColor: '#F9FAFB'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.r4r.greyLight
  }
} as const

const linkButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '13px',
  color: theme.r4r.azure,
  ':hover': { backgroundColor: 'transparent', color: theme.r4r.navy }
} as const

const ResetPassword: FC = () => {
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const navigation = useNavigate()
  const { passwordRequestReset, isSuccess, isError } = useUserPasswordRequestReset()

  const form = useFormik({
    initialValues: {
      email: ''
    },
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email(
          formatMessage({
            id: 'validation.email',
            defaultMessage: 'Niepoprawny adres email'
          })
        )
        .required(
          formatMessage({
            id: 'validation.required',
            defaultMessage: 'Wymagane'
          })
        )
    }),
    onSubmit: async (values, formik) => {
      formik.setSubmitting(true)
      await passwordRequestReset(values.email)
      formik.setSubmitting(false)
    }
  })

  useEffect(() => {
    queryClient.invalidateQueries()
    // eslint-disable-next-line
  }, [])

  if (isSuccess || isError) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Box
          sx={{
            width: '56px',
            height: '56px',
            mx: 'auto',
            mb: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: theme.r4r.gradient
          }}>
          <MarkEmailReadOutlined sx={{ color: '#fff', fontSize: '28px' }} />
        </Box>
        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: theme.r4r.navy, mb: '8px' }}>
          {formatMessage({
            id: 'resetPassword.link',
            defaultMessage: 'Link został wysłany.'
          })}
        </Typography>
        <Typography sx={{ fontSize: '13px', color: theme.r4r.grey, mb: '20px' }}>
          {formatMessage({
            id: 'resetPassword.linkHint',
            defaultMessage: 'Sprawdź skrzynkę e-mail i kliknij link, aby ustawić nowe hasło.'
          })}
        </Typography>
        <Button onClick={() => navigation('/auth/sign-in')} fullWidth sx={linkButtonSx}>
          {formatMessage({
            id: 'resetPassword.signIn',
            defaultMessage: 'Wróć do logowania'
          })}
        </Button>
      </Box>
    )
  }

  return (
    <Box component='form' onSubmit={form.handleSubmit} sx={{ textAlign: 'left' }}>
      <Typography
        sx={{
          fontSize: '22px',
          fontWeight: 700,
          color: theme.r4r.navy,
          textAlign: 'center',
          mb: '6px'
        }}>
        {formatMessage({ id: 'resetPassword.title', defaultMessage: 'Resetuj hasło' })}
      </Typography>
      <Typography
        sx={{
          fontSize: '13px',
          color: theme.r4r.grey,
          textAlign: 'center',
          mb: '8px'
        }}>
        {formatMessage({
          id: 'resetPassword.subtitle',
          defaultMessage: 'Podaj e-mail, a wyślemy link do zmiany hasła'
        })}
      </Typography>

      <FieldLabel>
        {formatMessage({ id: 'resetPassword.form.email', defaultMessage: 'Email' })}
      </FieldLabel>
      <TextField
        name='email'
        value={form.values.email}
        onChange={form.handleChange}
        fullWidth
        placeholder={formatMessage({
          id: 'resetPassword.form.email',
          defaultMessage: 'Email'
        })}
        disabled={form.isSubmitting}
        sx={fieldSx}
        error={!!form.errors.email}
        helperText={!!form.errors.email && form.errors.email}
      />

      <LoadingButton
        fullWidth
        type='submit'
        loading={form.isSubmitting}
        sx={{ ...submitButtonSx, mt: '28px', mb: '12px' }}>
        {formatMessage({
          id: 'resetPassword.form.submit',
          defaultMessage: 'Resetuj hasło'
        })}
      </LoadingButton>
      <Button onClick={() => navigation('/auth/sign-in')} fullWidth sx={linkButtonSx}>
        {formatMessage({
          id: 'resetPassword.signIn',
          defaultMessage: 'Wróć do logowania'
        })}
      </Button>
    </Box>
  )
}

export default ResetPassword
